import type { StepBand } from './categoryBands';
import type { PartItem } from './partsRun';
import { makesOwnHole, type StepHole } from './stepHoles';
import type { ByHoles } from './stepHolesRun';
import { subStepLabel } from './stepsRun';
import { editsOf, type Job3D } from './threeDApi';

// A POINTED SCREW'S HOLE, THERE ONCE IT IS DRIVEN (Louis 2026-10-29). The screw makes its own hole, so
// none is drilled before it; once it is in, the hole is in the wood. The part is drilled with them
// last, each its own entry (see drillPart), and a sub-step before the one that drives them draws the
// part as it stood before them — from its history (see meshAtSrc).
//
// EACH OF A PART'S POINTED DRILLINGS, by where it is on the history and the sub-step that drives its
// screws ("3-2"), or null where no sub-step says — with the hardware that drives it and how far from
// home that hardware is when its hole is in the wood (see holeOpensAt).
export type PointedDrilling = { index: number; label: string | null; via: string; mm: number };

// `labelOf`: the sub-step a hole of step `num` is driven in, where not the one it names (see useClips).
export function pointedDrillings(
  job: Job3D, byStep: ByHoles, steps: StepBand[], items: PartItem[],
  labelOf: (num: number, hole: StepHole) => string = (num, hole) => subStepLabel(num, hole.at),
): PointedDrilling[] {
  const holes = new Map<string, { hole: StepHole; label: string | null }>();
  for (const [category, found] of Object.entries(byStep)) {
    const num = steps.find((s) => s.category === category)?.num;
    found.holes.forEach((hole) => holes.set(hole.id, {
      hole, label: num != null && hole.at ? labelOf(num, hole) : null,
    }));
  }
  return editsOf(job).flatMap((e, index) => {
    if (e.op !== 'drill_all') return [];
    const mine = (e.bores ?? []).flatMap((b) => (b.id && holes.get(b.id) ? [{ ...holes.get(b.id)!, bore: b }] : []));
    if (!mine.length || !mine.every(({ hole }) => hole.via != null && makesOwnHole(items[Number(hole.via)]))) return [];
    return [{
      index,
      label: mine.find((m) => m.label)?.label ?? null,
      via: mine[0].hole.via!,
      mm: Math.max(...mine.map(({ hole, bore }) => holeOpensAt(hole, bore, items))),
    }];
  });
}

// HOW FAR FROM HOME ITS SCREW IS WHEN THE HOLE IS IN THE WOOD (Louis 2026-10-30), in millimetres:
// where its head touches the face, in the hole its head sits in — sunk, the counterbore's depth; through,
// the countersink's, a 90° cone from the bore out to the head — or where its point reaches the face, in
// the one it ends in, as deep as it goes. Before that the wood is whole; the hole drawn open as the screw
// came at it showed a hole no screw had made.
function holeOpensAt(hole: StepHole, bore: { diameter: number; depth: number | null }, items: PartItem[]): number {
  if (hole.sunkMm) return hole.sunkMm;
  if (hole.through) {
    // As wide as the head the hardware prints, or twice the bore (see boreRequest).
    const head = items[Number(hole.via)]?.size?.headDiaMm ?? bore.diameter * 2;
    return Math.max(0, (head - bore.diameter) / 2);
  }
  return bore.depth ?? hole.depthMm ?? 0;
}

// WHICH STATE OF THE PART A SUB-STEP DRAWS, as an edit count: before the first pointed drilling whose
// screws are driven LATER than it, or null — the part as it stands — where none is. Only while every
// entry after it is a pointed drilling too: a part changed after them has no such state to show.
export function pointedStateAt(
  drillings: PointedDrilling[], job: Job3D, label: string, order: Map<string, number>,
): number | null {
  const here = order.get(label);
  if (here == null) return null;
  const later = drillings.find((d) => d.label != null && (order.get(d.label) ?? -1) > here);
  if (!later) return null;
  const tail = editsOf(job).slice(later.index);
  return tail.every((_, k) => drillings.some((d) => d.index === later.index + k)) ? later.index : null;
}

// THE ONES THE SUB-STEP ITSELF DRIVES, each with the state before it — what the part is drawn at
// until that drilling's screw is in (see ClipPart.driven). Only where they are in a row on the
// history and what follows them is the state the sub-step ends on: each is then the one before plus
// one hole, in the order the screws go in. Else none, and the part is drawn as the sub-step ends.
export function pointedDriven(
  drillings: PointedDrilling[], job: Job3D, label: string, order: Map<string, number>,
): PointedDrilling[] {
  const mine = drillings.filter((d) => d.label === label);
  if (!mine.length || mine.some((d, k) => d.index !== mine[0].index + k)) return [];
  const end = pointedStateAt(drillings, job, label, order) ?? editsOf(job).length;
  return end === mine[0].index + mine.length ? mine : [];
}
